import React, { useState } from "react";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Zap,
  Waves,
  Battery,
  GitBranch,
} from "lucide-react";

const CircuitLearningMap = () => {
  // 节点展开状态管理
  const [expandedNodes, setExpandedNodes] = useState({
    staticElectricity: true,
    lightning: false,
    current: false,
    whyFlow: false,
    voltage: false,
    powerSource: false,
    resistor: false,
    circuit: false,
    stableCurrent: false,
    forceAnalysis: false,
    energyAnalysis: false,
    ohmsLaw: false,
    circuitStates: false,
    circuitStructures: false,
    circuitLaws: false,
  });

  // 类比内容展开状态管理
  const [expandedAnalogies, setExpandedAnalogies] = useState({
    waterFlow: false,
    heightDifference: false,
    waterPressure: false,
    waterPump: false,
    friction: false,
    forceBalance: false,
    energyLoss: false,
  });

  // 测试模式状态
  const [testMode, setTestMode] = useState(false);
  const [showingAnswer, setShowingAnswer] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // 测试问题数组
  const testQuestions = [
    {
      question: "电流的方向是怎样的？为什么会这样流动？",
      answer:
        "正电荷从高电位流向低电位。这是因为电场力驱动电荷从高电位移动到低电位，类似于水流从高处流向低处。注意：电路中实际移动的是负电荷（电子）。",
    },
    {
      question: "为什么稳定电路中电流处处相等？",
      answer:
        "如果电路中某处电流不等于其他地方，就意味着电荷在某处积累或减少，会导致电路不稳定。",
    },
    {
      question: "在完全无电阻的导线中，为什么不需要电压降也能维持电流？",
      answer:
        "在理想无阻导线中，电荷运动不受阻力，因此不需要电场力(电压)来维持运动。类似于无摩擦环境下物体保持匀速运动不需要外力。根据欧姆定律U=IR，如果R=0，则U=0。",
    },
    {
      question: "串联电路和并联电路的主要区别是什么？它们各有什么特点？",
      answer:
        "串联电路：元件首尾相连，电流只有一条通路。特点是电流处处相等，电压等于各部分电压之和，总电阻等于各电阻之和。\n并联电路：元件头尾相接，电流有多条通路。特点是各并联部分电压相等，干路电流等于各支路电流之和，总电阻的倒数等于各电阻倒数之和。",
    },
    {
      question: "电源在电路中起什么作用？它与电压有什么关系？",
      answer:
        "电源是将其他形式的能量转化为电能的装置，在电路中起到提供电能的作用。电源通过外力将电荷从低电位搬运到高电位，形成电势能差（即电压）。电源是电路中电位上升的部分，为电路提供了驱动电流所需的能量。",
    },
    {
      question:
        "电阻对电路有什么影响？根据欧姆定律，电流、电压和电阻之间有什么关系？",
      answer:
        "电阻限制电流的大小，阻碍电荷的流动，并将电能转化为热能。根据欧姆定律(U=IR)，在固定电压下，电阻越大，电流越小；在固定电阻下，电压越大，电流越大。电阻是产生电压降的必要条件，电流流过电阻时会消耗电能。",
    },
    {
      question: "如何解释电路中的能量转换过程？",
      answer:
        "在完整电路中，能量转换遵循能量守恒定律。电源将化学能、机械能等转换为电能，提高电荷的电势能；电荷在电路中流动时，电势能在电阻处转换为热能或其他形式的能量（如灯泡发光、电机转动）。整个过程中，能量总量保持不变，只是形式发生了转换。",
    },
  ];

  // 节点展开逻辑
  const toggleNode = (nodeId) => {
    setExpandedNodes({
      ...expandedNodes,
      [nodeId]: !expandedNodes[nodeId],
    });
  };

  // 类比展开逻辑
  const toggleAnalogy = (analogyId) => {
    setExpandedAnalogies({
      ...expandedAnalogies,
      [analogyId]: !expandedAnalogies[analogyId],
    });
  };

  // 切换测试模式
  const toggleTestMode = () => {
    setTestMode(!testMode);
    setShowingAnswer(false);
    setCurrentQuestion(0);
    // 切换测试模式时折叠所有节点
    if (!testMode) {
      const collapsedState = {};
      Object.keys(expandedNodes).forEach((key) => {
        collapsedState[key] = false;
      });
      setExpandedNodes(collapsedState);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < testQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowingAnswer(false);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowingAnswer(false);
    }
  };

  // 检查节点是否可以展开（考核模式：只有前一个节点展开后才能展开当前节点）
  const canExpand = (nodeId) => {
    const nodeOrder = [
      "staticElectricity",
      "lightning",
      "current",
      "whyFlow",
      "voltage",
      "powerSource",
      "resistor",
      "circuit",
      "stableCurrent",
      "forceAnalysis",
      "energyAnalysis",
      "ohmsLaw",
      "circuitStates",
      "circuitStructures",
      "circuitLaws",
    ];
    const nodeIndex = nodeOrder.indexOf(nodeId);
    if (nodeIndex === 0) return true;
    return expandedNodes[nodeOrder[nodeIndex - 1]];
  };

  const nodeStyles = {
    primary: "bg-blue-100 border-blue-500",
    analysis: "bg-green-100 border-green-500",
    law: "bg-purple-100 border-purple-500",
    concept: "bg-yellow-100 border-yellow-500",
  };

  const analogyStyle =
    "ml-8 mt-2 p-3 border-l-2 border-orange-400 bg-orange-50 rounded-r-md text-sm";

  return (
    <div className="p-4 max-w-3xl mx-auto font-sans">
      <h1 className="text-2xl font-bold text-center mb-6">
        初中电路原理-概念复习
      </h1>

      <div className="flex justify-center space-x-4 mb-4">
        <button
          onClick={toggleTestMode}
          className={`px-4 py-2 rounded-md font-medium ${
            testMode
              ? "bg-blue-100 text-blue-700 border border-blue-300"
              : "bg-green-100 text-green-700 border border-green-300"
          }`}
        >
          {testMode ? "返回学习模式" : "进入测试模式"}
        </button>
      </div>

      {testMode ? (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="mb-4 text-sm text-gray-500 text-center">
            问题 {currentQuestion + 1} / {testQuestions.length}
          </div>
          <h2 className="text-xl font-semibold mb-4">
            {testQuestions[currentQuestion].question}
          </h2>
          {showingAnswer ? (
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <p className="whitespace-pre-line">
                {testQuestions[currentQuestion].answer}
              </p>
            </div>
          ) : (
            <button
              onClick={() => setShowingAnswer(true)}
              className="w-full py-2 bg-blue-600 text-white rounded-md mb-4 hover:bg-blue-700"
            >
              显示答案
            </button>
          )}
          <div className="flex justify-between">
            <button
              onClick={prevQuestion}
              className={`px-4 py-2 rounded-md ${
                currentQuestion === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-100 text-blue-700 hover:bg-blue-200"
              }`}
              disabled={currentQuestion === 0}
            >
              上一题
            </button>
            <button
              onClick={nextQuestion}
              className={`px-4 py-2 rounded-md ${
                currentQuestion === testQuestions.length - 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-100 text-blue-700 hover:bg-blue-200"
              }`}
              disabled={currentQuestion === testQuestions.length - 1}
            >
              下一题
            </button>
          </div>
        </div>
      ) : (
        <p className="mb-4 text-gray-600 text-center">
          点击每个知识点展开内容，按照顺序学习和检验你的理解
        </p>
      )}

      {!testMode && (
        <div className="space-y-4">
          {/* 1. 静电现象 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${nodeStyles.concept}`}
          >
            <div
              className="flex items-center cursor-pointer"
              onClick={() => toggleNode("staticElectricity")}
            >
              {expandedNodes.staticElectricity ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Zap size={20} className="mr-2 text-yellow-600" />
              <h2 className="text-lg font-semibold">静电现象</h2>
            </div>
            {expandedNodes.staticElectricity && (
              <div className="ml-8 mt-2">
                <p>两种不同物体摩擦产生电荷分离</p>
                <p className="mt-2 text-sm text-gray-600">
                  例如：摩擦玻璃棒产生正电荷，摩擦橡胶棒产生负电荷
                </p>
                <div className="mt-3 p-2 bg-blue-50 rounded-md text-sm">
                  <p className="font-medium text-blue-800">思考问题：</p>
                  <p className="mt-1">
                    为什么摩擦物体会产生静电？这与物质内部结构有什么关系？
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 2. 雷电 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("lightning") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("lightning") ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => canExpand("lightning") && toggleNode("lightning")}
            >
              {expandedNodes.lightning ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Zap size={20} className="mr-2 text-yellow-600" />
              <h2 className="text-lg font-semibold">雷电</h2>
              {!canExpand("lightning") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.lightning && (
              <div className="ml-8 mt-2">
                <p>云层与地面间大规模放电现象</p>
                <p className="mt-2 text-sm text-gray-600">
                  雷电是静电现象的一种放大形式，云层中的电荷积累到一定程度后迅速放电
                </p>
                <div className="mt-3 p-2 bg-blue-50 rounded-md text-sm">
                  <p className="font-medium text-blue-800">思考问题：</p>
                  <p className="mt-1">
                    为什么高大建筑物需要安装避雷针？避雷针的工作原理是什么？
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 3. 电流 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("current") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("current") ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => canExpand("current") && toggleNode("current")}
            >
              {expandedNodes.current ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Waves size={20} className="mr-2 text-blue-600" />
              <h2 className="text-lg font-semibold">电流</h2>
              {!canExpand("current") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.current && (
              <div className="ml-8 mt-2">
                <p>电流是电荷的定向移动</p>
                <p className="mt-2 text-sm text-gray-600">单位：安培（I）</p>
                <p className="mt-2 text-sm text-gray-600">
                  注意：规定电流方向是正电荷移动方向，实际导体中常是负电荷(电子)移动
                </p>
              </div>
            )}
          </div>

          {/* 4. 电流为什么会流动 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.primary
            } ${!canExpand("whyFlow") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("whyFlow") ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => canExpand("whyFlow") && toggleNode("whyFlow")}
            >
              {expandedNodes.whyFlow ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Lightbulb size={20} className="mr-2 text-yellow-600" />
              <h2 className="text-lg font-semibold">电流为什么会流动？</h2>
              {!canExpand("whyFlow") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.whyFlow && (
              <div className="ml-8 mt-2">
                <p>正电荷从高电位流向低电位，是由电压(电场力)驱动的</p>
                <div
                  className="flex items-center mt-2 text-orange-600 cursor-pointer"
                  onClick={() => toggleAnalogy("waterFlow")}
                >
                  {expandedAnalogies.waterFlow ? (
                    <ChevronDown size={16} className="mr-1" />
                  ) : (
                    <ChevronRight size={16} className="mr-1" />
                  )}
                  <span className="text-sm font-medium">类比：水流</span>
                </div>
                {expandedAnalogies.waterFlow && (
                  <div className={analogyStyle}>
                    <p>水往低处流，（正）电荷往低电位流</p>
                    <p className="mt-1">
                      水流因高度差(重力)而动，电流因电位差(电场力)而动
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 5. 电压 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("voltage") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("voltage") ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => canExpand("voltage") && toggleNode("voltage")}
            >
              {expandedNodes.voltage ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Zap size={20} className="mr-2 text-blue-600" />
              <h2 className="text-lg font-semibold">电压</h2>
              {!canExpand("voltage") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.voltage && (
              <div className="ml-8 mt-2">
                <p>电压的本质是电位差，是推动电流的“驱动力”</p>
                <p className="mt-1 text-sm text-gray-600">单位：伏特（U）</p>
                <div
                  className="flex items-center mt-2 text-orange-600 cursor-pointer"
                  onClick={() => toggleAnalogy("heightDifference")}
                >
                  {expandedAnalogies.heightDifference ? (
                    <ChevronDown size={16} className="mr-1" />
                  ) : (
                    <ChevronRight size={16} className="mr-1" />
                  )}
                  <span className="text-sm font-medium">类比：高度差</span>
                </div>
                {expandedAnalogies.heightDifference && (
                  <div className={analogyStyle}>
                    <p>高度差导致物体下落，电位差导致电荷移动</p>
                  </div>
                )}
                <div
                  className="flex items-center mt-2 text-orange-600 cursor-pointer"
                  onClick={() => toggleAnalogy("waterPressure")}
                >
                  {expandedAnalogies.waterPressure ? (
                    <ChevronDown size={16} className="mr-1" />
                  ) : (
                    <ChevronRight size={16} className="mr-1" />
                  )}
                  <span className="text-sm font-medium">类比：水压</span>
                </div>
                {expandedAnalogies.waterPressure && (
                  <div className={analogyStyle}>
                    <p>水管内水压推着“水”流动，电路中电压推着“电荷”流动</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 6. 电源 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("powerSource") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("powerSource")
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                canExpand("powerSource") && toggleNode("powerSource")
              }
            >
              {expandedNodes.powerSource ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Battery size={20} className="mr-2 text-green-600" />
              <h2 className="text-lg font-semibold">电源</h2>
              {!canExpand("powerSource") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.powerSource && (
              <div className="ml-8 mt-2">
                <p>电源提供能量，将电荷从低电位搬运到高电位</p>
                <p className="mt-1 text-sm text-gray-600">
                  例如：电池通过化学反应产生电位差
                </p>
                <div
                  className="flex items-center mt-2 text-orange-600 cursor-pointer"
                  onClick={() => toggleAnalogy("waterPump")}
                >
                  {expandedAnalogies.waterPump ? (
                    <ChevronDown size={16} className="mr-1" />
                  ) : (
                    <ChevronRight size={16} className="mr-1" />
                  )}
                  <span className="text-sm font-medium">类比：水泵</span>
                </div>
                {expandedAnalogies.waterPump && (
                  <div className={analogyStyle}>
                    <p>电源类似于水泵，将水从低处抽到高处</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 7. 电阻 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("resistor") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("resistor") ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => canExpand("resistor") && toggleNode("resistor")}
            >
              {expandedNodes.resistor ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Zap size={20} className="mr-2 text-red-600" />
              <h2 className="text-lg font-semibold">电阻</h2>
              {!canExpand("resistor") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.resistor && (
              <div className="ml-8 mt-2">
                <p>电阻阻碍电荷运动，将电能转化为热能</p>
                <p className="mt-1 text-sm text-gray-600">单位：欧姆（R）</p>
                <div
                  className="flex items-center mt-2 text-orange-600 cursor-pointer"
                  onClick={() => toggleAnalogy("friction")}
                >
                  {expandedAnalogies.friction ? (
                    <ChevronDown size={16} className="mr-1" />
                  ) : (
                    <ChevronRight size={16} className="mr-1" />
                  )}
                  <span className="text-sm font-medium">类比：摩擦力</span>
                </div>
                {expandedAnalogies.friction && (
                  <div className={analogyStyle}>
                    <p>电阻类似于摩擦力，阻碍运动并产生热量</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 8. 完整电路 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.primary
            } ${!canExpand("circuit") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("circuit") ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => canExpand("circuit") && toggleNode("circuit")}
            >
              {expandedNodes.circuit ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Battery size={20} className="mr-2 text-green-600" />
              <h2 className="text-lg font-semibold">完整电路</h2>
              {!canExpand("circuit") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.circuit && (
              <div className="ml-8 mt-2">
                <p>电路中必然同时存在电位下降和上升部分</p>
                <p className="mt-1">
                  电源：通过外力将(正)电荷从低电位搬运到高电位，获得电势能
                </p>
                <p className="mt-1">
                  用电器：(正)电荷从高电位流向低电位，电势能转化为其他形式的能量
                </p>
              </div>
            )}
          </div>

          {/* 9. 稳定电流 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("stableCurrent") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("stableCurrent")
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                canExpand("stableCurrent") && toggleNode("stableCurrent")
              }
            >
              {expandedNodes.stableCurrent ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Waves size={20} className="mr-2 text-blue-600" />
              <h2 className="text-lg font-semibold">稳定电流特性</h2>
              {!canExpand("stableCurrent") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.stableCurrent && (
              <div className="ml-8 mt-2">
                <p>稳态电路中各处电流保持恒定</p>
                <p className="mt-1 text-sm text-gray-600">
                  如果不恒定，电荷会局部积累或减少，电路不稳定
                </p>
                <p className="mt-2">为什么电流能保持稳定？有三个角度分析：</p>
                <div className="ml-4 flex items-center mt-1">
                  <ArrowRight size={16} className="mr-1 text-blue-500" />
                  <span>受力分析角度</span>
                </div>
                <div className="ml-4 flex items-center mt-1">
                  <ArrowRight size={16} className="mr-1 text-blue-500" />
                  <span>能量分析角度</span>
                </div>
                <div className="ml-4 flex items-center mt-1">
                  <ArrowRight size={16} className="mr-1 text-blue-500" />
                  <span>欧姆定律角度</span>
                </div>
              </div>
            )}
          </div>

          {/* 10. 受力分析角度 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.analysis
            } ${!canExpand("forceAnalysis") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("forceAnalysis")
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                canExpand("forceAnalysis") && toggleNode("forceAnalysis")
              }
            >
              {expandedNodes.forceAnalysis ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Lightbulb size={20} className="mr-2 text-purple-600" />
              <h2 className="text-lg font-semibold">受力分析角度</h2>
              {!canExpand("forceAnalysis") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.forceAnalysis && (
              <div className="ml-8 mt-2">
                <p>导线中：电荷运动几乎不受阻碍，不需要电位差维持运动</p>
                <p className="mt-1">
                  电阻中：需要电场力与阻力平衡，因此产生电压降
                </p>
                <div
                  className="flex items-center mt-2 text-orange-600 cursor-pointer"
                  onClick={() => toggleAnalogy("forceBalance")}
                >
                  {expandedAnalogies.forceBalance ? (
                    <ChevronDown size={16} className="mr-1" />
                  ) : (
                    <ChevronRight size={16} className="mr-1" />
                  )}
                  <span className="text-sm font-medium">类比：力的平衡</span>
                </div>
                {expandedAnalogies.forceBalance && (
                  <div className={analogyStyle}>
                    <p>类似于物体在粗糙平面上匀速运动，需要外力克服摩擦力</p>
                    <p className="mt-1">
                      如果有外力、没有阻力，物体会加速；如果有阻力、但没有足够的外力，物体会减速
                    </p>
                    <p className="mt-1">
                      电路中，电场力与阻力平衡，保证电荷匀速运动
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 11. 能量分析角度 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.analysis
            } ${!canExpand("energyAnalysis") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("energyAnalysis")
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                canExpand("energyAnalysis") && toggleNode("energyAnalysis")
              }
            >
              {expandedNodes.energyAnalysis ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Lightbulb size={20} className="mr-2 text-purple-600" />
              <h2 className="text-lg font-semibold">能量分析角度</h2>
              {!canExpand("energyAnalysis") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.energyAnalysis && (
              <div className="ml-8 mt-2">
                <p>导线中：几乎没有能量损耗，电荷动能保持不变</p>
                <p className="mt-1">
                  电阻中：电荷运动产热(能量损耗)，必须通过电压降(消耗电势能)来补偿能量损耗
                </p>
                <div
                  className="flex items-center mt-2 text-orange-600 cursor-pointer"
                  onClick={() => toggleAnalogy("energyLoss")}
                >
                  {expandedAnalogies.energyLoss ? (
                    <ChevronDown size={16} className="mr-1" />
                  ) : (
                    <ChevronRight size={16} className="mr-1" />
                  )}
                  <span className="text-sm font-medium">类比：能量损耗</span>
                </div>
                {expandedAnalogies.energyLoss && (
                  <div className={analogyStyle}>
                    <p>类似于摩擦生热，电阻使电势能转化为热能</p>
                    <p className="mt-1">
                      就像下坡的车，阻力做负功，重力做正功，车速保持不变
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 12. 欧姆定律角度 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${nodeStyles.law} ${
              !canExpand("ohmsLaw") ? "opacity-50" : ""
            }`}
          >
            <div
              className={`flex items-center ${
                canExpand("ohmsLaw") ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => canExpand("ohmsLaw") && toggleNode("ohmsLaw")}
            >
              {expandedNodes.ohmsLaw ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Lightbulb size={20} className="mr-2 text-red-600" />
              <h2 className="text-lg font-semibold">欧姆定律角度</h2>
              {!canExpand("ohmsLaw") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.ohmsLaw && (
              <div className="ml-8 mt-2">
                <p>U = I × R：只有同时有电流和电阻时，才有电压</p>
                <p className="mt-2">特殊情况：</p>
                <div className="ml-4 mt-1">
                  <p>① 通路时：导线相连处，电阻≈0，所以电位处处相等</p>
                  <p className="mt-1">
                    ② 断路时：I=0，相连部分(即使有电阻)电位处处相等
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 13. 电路状态 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("circuitStates") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("circuitStates")
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                canExpand("circuitStates") && toggleNode("circuitStates")
              }
            >
              {expandedNodes.circuitStates ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Battery size={20} className="mr-2 text-blue-600" />
              <h2 className="text-lg font-semibold">基本电路状态</h2>
              {!canExpand("circuitStates") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.circuitStates && (
              <div className="ml-8 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium">通路</p>
                    <p className="text-sm mt-1">电路连通，电流正常</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium">开路/断路</p>
                    <p className="text-sm mt-1">电路中断，无法流通</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium">短路</p>
                    <p className="text-sm mt-1">
                      负载被低阻通路(R≈0)替代，若电压不变→电流剧增
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 14. 电路结构 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${
              nodeStyles.concept
            } ${!canExpand("circuitStructures") ? "opacity-50" : ""}`}
          >
            <div
              className={`flex items-center ${
                canExpand("circuitStructures")
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                canExpand("circuitStructures") &&
                toggleNode("circuitStructures")
              }
            >
              {expandedNodes.circuitStructures ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <GitBranch size={20} className="mr-2 text-blue-600" />
              <h2 className="text-lg font-semibold">基本电路结构</h2>
              {!canExpand("circuitStructures") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.circuitStructures && (
              <div className="ml-8 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium">串联</p>
                    <p className="text-sm mt-1">
                      所有元件依次连接，电流一致，电压分配
                    </p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium">并联</p>
                    <p className="text-sm mt-1">各支路电压相等，电流分流</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 15. 电路规律 */}
          <div
            className={`p-4 border-l-4 rounded-md shadow-sm ${nodeStyles.law} ${
              !canExpand("circuitLaws") ? "opacity-50" : ""
            }`}
          >
            <div
              className={`flex items-center ${
                canExpand("circuitLaws")
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                canExpand("circuitLaws") && toggleNode("circuitLaws")
              }
            >
              {expandedNodes.circuitLaws ? (
                <ChevronDown size={20} className="mr-2" />
              ) : (
                <ChevronRight size={20} className="mr-2" />
              )}
              <Lightbulb size={20} className="mr-2 text-red-600" />
              <h2 className="text-lg font-semibold">电路规律</h2>
              {!canExpand("circuitLaws") && (
                <span className="ml-2 text-xs text-gray-500">
                  (请先学习上一节)
                </span>
              )}
            </div>
            {expandedNodes.circuitLaws && (
              <div className="ml-8 mt-2">
                <p className="font-medium">串联电路规律：</p>
                <ul className="list-disc ml-6 mt-1">
                  <li>电流处处相等：I₁ = I₂ = ... = I</li>
                  <li>电压等于各部分电压之和：U = U₁ + U₂ + ...</li>
                  <li>总电阻等于各电阻之和：R = R₁ + R₂ + ...</li>
                </ul>
                <p className="font-medium mt-3">并联电路规律：</p>
                <ul className="list-disc ml-6 mt-1">
                  <ul className="list-disc ml-6 mt-1">
                    <li>干路电流等于各支路电流之和：I = I₁ + I₂ + ...</li>
                    <li>各并联部分电压相等：U₁ = U₂ = ... = U</li>
                    <li>
                      总电阻的倒数等于各电阻倒数之和：1/R = 1/R₁ + 1/R₂ + ...
                    </li>
                  </ul>
                </ul>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="font-medium text-blue-800">思考问题：</p>
                  <ul className="list-disc ml-6 mt-1 text-sm">
                    <li>为什么家庭电路多采用并联方式连接电器？</li>
                    <li>短路为什么危险？从能量角度如何解释？</li>
                    <li>
                      当某灯泡烧断时，串联与并联电路中其他灯泡状态如何变化？
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-8 pt-4 border-t border-gray-200 text-center text-xs text-gray-500">
        <p>© 2025 孙维刚教育研究院-陈硕老师，版权所有，保留一切权利。</p>
        <p className="mt-1">本资料仅限于教育目的使用。</p>
      </div>
    </div>
  );
};

export default CircuitLearningMap;
